import { useMemo } from "react";
import type { ElementType } from "react";
import { useSelector } from "react-redux";
import { TrendingUp, Moon, DollarSign, PiggyBank } from "lucide-react";
import { Skeleton, Carousel } from "@hotelise/ui";

import { getReservationsReport } from "state/modules/reservations";

interface SummaryCard {
	title: string;
	value: string;
	icon: ElementType;
}

const SKELETON_CARDS = ["a", "b", "c", "d"];

const SkeletonCard = () => (
	<div className="bg-card rounded-lg p-4 border">
		<div className="flex items-center justify-between mb-2">
			<Skeleton className="h-4 w-24" />
			<Skeleton className="h-4 w-4 rounded" />
		</div>
		<Skeleton className="h-8 w-28" />
	</div>
);

const MetricCard = ({
	metric,
	className,
}: {
	metric: SummaryCard;
	className?: string;
}) => {
	const Icon = metric.icon;
	return (
		<div
			className={`bg-card rounded-lg p-4 border hover:shadow-sm transition-shadow ${className || ""}`}
		>
			<div className="flex items-center justify-between mb-2">
				<h3 className="text-sm font-medium text-muted-foreground">
					{metric.title}
				</h3>
				<Icon className="h-4 w-4 text-muted-foreground" />
			</div>
			<p className="text-2xl font-bold">{metric.value}</p>
		</div>
	);
};

interface Props {
	isLoading: boolean;
	isMobile: boolean;
}

const ReservationsSummary = (props: Props) => {
	const { isLoading, isMobile } = props;

	const report = useSelector(getReservationsReport);

	const summaryItems: SummaryCard[] = useMemo(() => {
		if (!report) return [];

		return [
			{
				title: "Occupancy",
				value: `${(Math.round((report.calculations?.occupancyRate || 0) * 100) / 100).toFixed(2)}%`,
				icon: TrendingUp,
			},
			{
				title: "Nights Sold",
				value: `${report.reportItems?.reduce((prevValue, currentValue) => {
					const { nights } = currentValue;
					return prevValue + nights;
				}, 0)}`,
				icon: Moon,
			},
			{
				title: "Owner Income",
				value: `$${report.calculations?.totalInCurrency.totalIncomeInCurrency || 0}`,
				icon: DollarSign,
			},
			{
				title: "Net Income (after exp)",
				value: `$${report.calculations?.totalInCurrency.netOwnerIncomeInCurrency || 0}`,
				icon: PiggyBank,
			},
		];
	}, [report]);

	if (isLoading) {
		if (isMobile) {
			return (
				<Carousel showDots={false}>
					{SKELETON_CARDS.map((id) => (
						<SkeletonCard key={id} />
					))}
				</Carousel>
			);
		}

		return (
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
				{SKELETON_CARDS.map((id) => (
					<SkeletonCard key={id} />
				))}
			</div>
		);
	}

	if (isMobile) {
		return (
			<Carousel slideClassName="flex-[0_0_70%] min-w-0">
				{summaryItems.map((metric) => (
					<MetricCard
						key={metric.title}
						metric={metric}
						className="h-full"
					/>
				))}
			</Carousel>
		);
	}

	return (
		<div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
			{summaryItems.map((metric) => (
				<MetricCard key={metric.title} metric={metric} />
			))}
		</div>
	);
};

export default ReservationsSummary;
